<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <div>
      <div
        v-if="showRefreshAppointments || currentClient.id"
        class="row"
      >
        <q-space />
        <q-btn
          v-if="showRefreshAppointments"
          :label="$t('home.buttonRefreshAppointments')"
          color="primary"
          flat
          size="sm"
          :disable="offlineMode"
          :loading="isRefreshingAppointments"
          @click="onRefreshAppointments"
        />
        <q-btn-dropdown
          v-if="currentClient.id && userCanChangeVisibility && !fakeRole"
          color="primary"
          flat
          size="sm"
          :label="roleLabel || $t('home.buttonSelectFakeRole')"
        >
          <q-list>
            <q-item
              v-for="(role, idx) in availableActiveRoles"
              :key="idx"
              v-close-popup
              clickable
              :class="{'text-primary' : role.value === roleValue}"
              @click="onChangeRole(role)"
            >
              <q-item-section>
                <q-item-section>
                  {{ role.label }}
                </q-item-section>
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
      <div v-if="isEmptyHomePage">
        <h5
          class="q-mt-none q-mb-sm"
          :data-e2e="'header-Welcome'"
        >
          {{ $t('home.textTitle') }}
        </h5>
        {{ $t('home.textSubtitle') }}
      </div>
      <banner
        v-if="userDetails.name === userDetails.email"
        show-icon
        type="warning"
        :message="$t('home.warningNameSameAsEmail')"
        class="q-my-sm"
      >
        <template #action>
          <q-btn
            flat
            @click="$router.push({ name: 'Settings' })"
          >
            {{ $t('home.buttonGoToAccountSettings') }}
          </q-btn>
        </template>
      </banner>
      <banner
        v-if="fakeRole"
        :message="$t('home.textUsingFakeRole', { role: roleLabel })"
        class="q-mb-sm"
      >
        <template #action>
          <q-btn-dropdown
            v-if="currentClient.id && userCanChangeVisibility && fakeRole"
            flat
            size="sm"
            :label="roleLabel"
          >
            <q-list>
              <div
                v-for="(role, idx) in availableActiveRoles"
                :key="idx"
              >
                <q-item
                  v-close-popup
                  clickable
                  @click="onChangeRole(role)"
                >
                  <q-item-section>
                    <q-item-section
                      :class="{
                        'text-weight-bold': role.value === userDetails.role,
                        'text-primary': role.value === roleValue
                      }"
                    >
                      {{ role.label }}
                    </q-item-section>
                  </q-item-section>
                  <q-item-section
                    v-if="role.value === userDetails.role"
                    avatar
                  >
                    <q-icon
                      name="person"
                      size="xs"
                    />
                  </q-item-section>
                </q-item>
                <q-separator v-if="idx === 0" />
              </div>
            </q-list>
          </q-btn-dropdown>
        </template>
      </banner>
      <div
        v-if="isOwnPage && hasUpcommingAppointments"
        class="q-mb-lg"
      >
        <div class="row col-12 items-center">
          <div
            class="text-overline q-mb-none q-pb-none text-grey-5 text-uppercase col"
          >
            {{ $t('home.textUpcommingAppointments') }}
          </div>
          <div class="col-auto">
            <q-btn
              :icon="loadPastUserAppointments ? 'update' : 'history'"
              flat
              dense
              round
              color="primary"
              @click="onChangeLoadPastUserAppointments"
            >
              <q-tooltip
                v-if="loadPastUserAppointments"
              >
                {{ $t('home.tooltipShowFutureAppointments') }}
              </q-tooltip>
              <q-tooltip
                v-else
              >
                {{ $t('home.tooltipShowPastAppointments') }}
              </q-tooltip>
            </q-btn>
          </div>
        </div>
        <div
          v-if="!isLoadingRunningAppointments"
          class="q-mt-md q-gutter-md"
        >
          <div
            v-for="appointment in filteredNextUserAppointments"
            :key="appointment.id"
          >
            <appointment-card
              :appointment="appointment"
              check-pending-actions
              :fake-role="fakeRole"
              @click="onClickAppointment(appointment)"
            />
          </div>
          <div
            v-if="hasMoreUserAppointments"
            class="full-width row justify-center"
          >
            <q-btn
              color="primary"
              flat
              data-e2e="button-show-more-appointments"
              :label="$t('home.buttonShowMoreAppointments')"
              :loading="isLoadingUserAppointments"
              @click="onLoadMoreUserAppointments"
            />
          </div>
        </div>
        <q-inner-loading :showing="isLoadingRunningAppointments" />
      </div>
      <div
        v-if="userCanSeeTaskLists && hasTaskLists"
        class="q-mb-lg"
      >
        <div
          class="text-overline q-mb-none q-pb-none text-grey-5 text-uppercase"
        >
          {{ $t('home.textTaskLists') }}
        </div>
        <div class="q-mt-md q-gutter-md">
          <div
            v-for="taskList in taskLists"
            :key="taskList.id"
          >
            <task-list-card
              :client="currentClient"
              :task-list="taskList"
              view-session-button
            />
          </div>
        </div>
      </div>
      <div
        v-if="showFormSessions"
        class="q-mb-lg"
      >
        <div class="q-mt-md q-gutter-md">
          <div
            v-for="session in formSessions"
            :key="session.id"
          >
            <form-session-card
              :session="session"
            />
          </div>
        </div>
      </div>
      <div
        v-if="showTrainingSessions"
        class="q-mb-lg"
      >
        <div
          class="text-overline q-mb-none q-pb-none text-grey-5 text-uppercase"
        >
          {{ $t('home.textTraining') }}
        </div>
        <div class="q-mt-md q-gutter-md">
          <div
            v-for="session in trainingSessions"
            :key="session.id"
          >
            <training-session-card
              :session="session"
              :data-e2e="'card-session-RBT-' + session.sessionName.replace(/ /g, '-')"
              @edit="onEditSession"
            />
          </div>
        </div>
      </div>
      <div
        v-if="hasActiveSessions"
        class="q-mb-lg"
      >
        <div class="q-mt-md q-gutter-md">
          <div
            v-for="session in sortedActiveSessions"
            :key="session.id"
          >
            <scheduled-session-card
              :session="session"
              :fake-role="fakeRole"
              :appointments="getSessionClientAppointments(session)"
              :initial-appointment="initialAppointment"
              @edit="onEditSession"
              @edit-schedule="onEditSessionSchedule"
            />
          </div>
        </div>
      </div>
      <div
        v-if="hasActiveSessions && !showScheduledSessions && !isEditingSession"
        class="row col-12 q-mb-lg"
      >
        <q-btn
          :label="$t('home.buttonShowScheduledSessions')"
          color="primary"
          flat
          class="col-12"
          @click="showScheduledSessions = true"
        />
      </div>
      <div
        v-else-if="hasScheduledSessions"
        class="q-mb-lg"
      >
        <div class="q-mt-md q-gutter-md">
          <div
            v-for="session in visibleScheduledSessions"
            :key="session.id"
          >
            <parent-session-card
              v-if="session.sessionType === 'remote_async'"
              :session="session"
              :using-fake-role="!!fakeRole"
              @edit="onEditSession"
            />
            <scheduled-session-card
              v-else
              :session="session"
              :fake-role="fakeRole"
              :appointments="getSessionClientAppointments(session)"
              :initial-appointment="initialAppointment"
              check-pending-actions
              @edit="onEditSession"
              @edit-schedule="onEditSessionSchedule"
            />
          </div>
        </div>
      </div>
      <div
        v-if="showPrograms"
        class="q-mb-lg"
      >
        <div
          class="row"
        >
          <q-space />
          <div
            v-if="userCanAddProgramToSession"
            class="col-auto"
          >
            <q-btn-dropdown
              v-if="hasSessions"
              flat
              :label="$t('home.buttonAddAllPrograms')"
              icon="playlist_add"
              color="primary"
              size="sm"
            >
              <q-list>
                <q-item
                  v-for="session in programCardSessions"
                  :key="session.id"
                  v-ripple
                  v-close-popup
                  clickable
                  class="items-center"
                  @click="onAddAllProgramsToSession(session)"
                >
                  <q-item-label>
                    {{ session.sessionName }}
                  </q-item-label>
                </q-item>
                <q-separator />
                <q-item
                  v-close-popup
                  v-ripple
                  clickable
                  class="items-center"
                  @click="onAddAllProgramsToSession()"
                >
                  <q-item-label>
                    {{ $t('programs.programCard.itemCreateNewSession') }}
                  </q-item-label>
                </q-item>
              </q-list>
            </q-btn-dropdown>
            <q-btn
              v-else
              flat
              :label="$t('home.buttonAddAllPrograms')"
              icon="playlist_add"
              color="primary"
              size="sm"
              @click="onAddAllProgramsToSession()"
            />
          </div>
        </div>
        <div
          v-for="treatmentArea in treatmentAreas"
          :key="treatmentArea.id"
          class="row col-12 q-mb-lg"
        >
          <div
            v-if="treatmentArea.id"
            class="col-12 text-overline q-mb-md q-pb-none text-dark-variant text-uppercase"
          >
            {{ treatmentArea.label }}
          </div>
          <div class="row col-12">
            <div
              v-for="subTreatmentArea in treatmentArea.subTreatmentAreas"
              :key="subTreatmentArea.id"
              class="row col-12 q-mb-lg"
            >
              <div
                class="col-12 text-bold text-dark q-mb-sm"
              >
                {{ subTreatmentArea.label }}
              </div>
              <div
                class="row col-12 q-col-gutter-md"
              >
                <program-card
                  v-for="program in getPrograms(treatmentArea.id, subTreatmentArea.id)"
                  :key="program.id"
                  :program="program"
                  :sessions="programCardSessions"
                  :show-actions="userCanAddProgramToSession"
                  :show-delete-program="userCanDeleteProgram"
                  :show-target-chips="programHasTargets(program)"
                  :action-color="programActionButtonColor(program)"
                  :action-label="programActionButtonLabel(program)"
                  :action-icon="programActionButtonIcon(program)"
                  :action-tooltip="programActionButtonTooltip(program)"
                  class="col-xs-12 col-sm-6 col-md-4"
                  :data-e2e="'card-program-' + String(program.programName).replace(/ /g, '-')"
                  @add-to-session="onAddProgram($event, program)"
                  @action="onProgramAction(program)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showActivities"
        class="q-mb-lg"
      >
        <div
          class="row text-overline q-mb-md q-pb-none text-grey-5 text-uppercase"
        >
          <div class="col">
            {{ $t('home.textActivity') }}
          </div>
        </div>
        <activity-feed
          :events="['programCreation', 'programModification', 'targetMastered', 'targetReopened']"
          dense
        />
      </div>
    </div>
    <session-builder
      v-if="hasCurrentClient"
      ref="sessionBuilder"
      :client="currentClient.id"
      :clinic="currentClient.clinic"
      @session-changed="onSessionBuilderChanged"
    />
  </q-page>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import ActivityFeed from 'components/Activity/ActivityFeed';
import ProgramCard from 'components/Programs/ProgramCard';
import ProgramInfo from 'components/Programs/ProgramInfo';
import AppointmentCard from 'components/Sessions/AppointmentCard';
import ScheduleSession from 'components/Sessions/Modals/ScheduleSession';
import ParentSessionCard from 'components/Sessions/ParentSessionCard';
import ScheduledSessionCard from 'components/Sessions/ScheduledSessionCard';
import SessionBuilder from 'components/Sessions/SessionBuilder';
import Banner from 'components/UI/Banner';
import { chain, concat, every, filter, forEach, get, has, isEmpty, omit, reject, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import ProgramsDB from 'src/fb/programs-db';
import SessionsDB from 'src/fb/sessions-db';
import TaskListsDB from 'src/fb/taskLists-db';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';
import { sessions } from 'src/store/sessions';
import { treatmentPlans } from 'src/store/treatmentPlans';
import { units } from 'src/store/units';
import { notifyErrorWithDetails, userRoles } from 'src/utils/AppUtils';
import { defineAsyncComponent } from 'vue';
import { useTime } from 'src/composables/time';

export default {
  name: 'Home',

  components: {
    ActivityFeed,
    AppointmentCard,
    ParentSessionCard,
    ProgramCard,
    ScheduledSessionCard,
    SessionBuilder,
    TaskListCard: defineAsyncComponent(() => import('components/Clients/TaskListCard')),
    TrainingSessionCard: defineAsyncComponent(() => import('components/Sessions/TrainingSessionCard')),
    FormSessionCard: defineAsyncComponent(() => import('components/Sessions/FormSessionCard')),
    Banner,
  },

  mixins: [Auth, Interactions, AppointmentsMixin, Toolbox],

  beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (from.name === 'signup' && vm.ssoProviders.includes(vm.provider)) {
        vm.notifySuccess(vm.$t('signup.signupComponent.notifySignupSuccess'));
      }
      if (vm.offlineMode) {
        await vm.loadRunningGroupSessions();
      }
      vm.loadTaskLists();
      vm.loadClinicUnits();
      vm.syncClients();
    });
  }, // beforeRouteEnter()

  setup() {
    const { addToDate } = useTime();
    return { addToDate };
  },

  data() {
    return {
      runningGroupSessionsIds: [],
      isRefreshingAppointments: false,
      isLoadingUserAppointments: false,
      isLoadingRunningAppointments: false,

      showScheduledSessions: false,
      showProgramInfo: false,
      shownProgram: null,

      editSession: {},
      initialAppointment: null,

      taskLists: [],

      fakeRole: null,
    };
  },

  computed: {
    ...mapState(auth, [
      'userDetails',
      'currentClient',
      'provider',
      'ssoProviders',
      'connectedClinic',
      'userAppointments',
      'userAppointmentsInProgress',
      'loadPastUserAppointments',
      'userAppointmentsLimit',
      'clientAppointmentsUpcoming',
      'clientAppointmentsInProgress',
      'clientAppointmentsRecurring',
      'showDefaultTreatmentAreas',
      'isParent',
      'isTrainer',
      'userPrivilegesList',
      'hasMoreUserAppointments',
    ]),
    ...mapState(programs, ['programs']),
    ...mapState(network, ['offlineMode', 'networkOnline']),
    ...mapState(sessions, ['scheduledSessions', 'supervisingSessions']),
    ...mapState(treatmentPlans, ['clinicTreatmentAreas']),
    ...mapState(clients, ['clients']),

    isOwnPage() {
      const currentClientId = get(this.currentClient, 'id', null);
      return !currentClientId
        || (
          (get(this.currentClient, 'type') === 'staff')
          && (currentClientId === get(this.userDetails, 'staffMember'))
        );
    }, // isOwnPage()

    isEmptyHomePage() {
      return !get(this.currentClient, 'id') && !this.hasUpcommingAppointments;
    },

    userAppointmentsCombined() {
      return chain(this.userAppointments)
        .concat(this.userAppointmentsInProgress)
        .compact()
        .filter((appointment) => (
          !appointment.client || this.clients.find((client) => (client.id === appointment.client))
        ))
        .sortBy('scheduledStartTimestamp')
        .value();
    },

    filteredNextUserAppointments() {
      if (!this.userAppointmentsCombined) {
        return [];
      }

      /*
      When a user signs up after a bulk invite, they do not have access to any clients but, at the same time, could
      see upcoming appointments, that are linked to them. It could lead to the button "Go to client" not working
      correctly and other bugs. So we filter such appointments out.
      */
      if (this.connectedClinic.integration === 'aloha') {
        return this.userAppointmentsCombined.filter((appointment) => {
          const res = this.clients.findIndex((client) => client.id === appointment.client);

          const staffOnlyAppointmentTypes = ['Drive Time', 'Break Time'];
          const isStaffOnlyAppointment = staffOnlyAppointmentTypes.includes(appointment?.integrationAppointmentType);

          return (res !== -1 || isStaffOnlyAppointment) && !this.runningGroupSessionsIds.includes(appointment.session);
        });
      }

      if (this.offlineMode) {
        return filter(
          this.userAppointmentsCombined,
          (appointment) => chain(appointment).get('recurringWeekdays').isEmpty().value()
            && appointment.type !== 'unavailability'
            && !this.runningGroupSessionsIds.includes(appointment.session),
        );
      }

      return filter(
        this.userAppointmentsCombined,
        (appointment) => chain(appointment).get('recurringWeekdays').isEmpty().value()
          && appointment.type !== 'unavailability',
      );
    },

    hasUpcommingAppointments() {
      return !isEmpty(this.filteredNextUserAppointments);
    },

    roleValue() {
      return get(this.fakeRole, 'value', null);
    }, // roleValue()

    roleLabel() {
      return get(this.fakeRole, 'label', null);
    }, // roleLabel()

    availableActiveRoles() {
      const maxLevel = chain(this.userPrivilegesList)
        .find({ value: this.userDetails.userPrivilege || this.userDetails.role })
        .get('level', 9999)
        .add(this.fakeRole ? 0 : 1)
        .value();

      return reject(
        this.userPrivilegesList,
        (userRole) => (userRole.level < maxLevel),
      );
    }, // availableActiveRoles()

    userRole() {
      return get(this.fakeRole, 'role') || this.userDetails.role;
    }, // userRole()

    userPrivilege() {
      return get(this.fakeRole, 'value') || this.userDetails.userPrivilege || this.userDetails.role;
    }, // userRole()

    hasSessions() {
      return size(this.programCardSessions) > 0;
    }, // hasSessions()

    userCanChangeVisibility() {
      return this.userHasPrivilege('sessions/editScheduledSessions');
    }, // userCanChangeVisibility()

    userCanAddProgramToSession() {
      return this.userHasPrivilege('sessions/editScheduledSessions');
    }, // userCanAddProgramToSession()

    userCanDeleteProgram() {
      return [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR].includes(this.userRole);
    }, // userCanDeleteProgram()

    activeStaffClient() {
      return get(this.currentClient, 'type') === 'staff' && get(this.currentClient, 'status') === 'active';
    }, // activeStaffClient()

    userCanSeeTaskLists() {
      return this.activeStaffClient && [
        userRoles.MANAGER,
        userRoles.ADMINISTRATOR,
        userRoles.SUPERVISOR,
        userRoles.SENIOR_THERAPIST,
      ].includes(this.userRole);
    }, // userCanSeeTaskLists()

    hasTaskLists() {
      return size(this.taskLists) > 0;
    }, // hasTaskLists()

    showFormSessions() {
      return size(this.formSessions) > 0;
    },

    showTrainingSessions() {
      return size(this.trainingSessions) > 0 && this.activeStaffClient;
    }, // showTrainingSessions()

    hasCurrentClient() {
      return has(this.currentClient, 'id');
    }, // hasCurrentClient()

    showPrograms() {
      if (this.isParent) {
        // Parents should not see prorams if all their sessions are remote async
        // data collection - makes the view simpler for them
        if (every(this.visibleSessions, { sessionType: 'remote_async' })) {
          return false;
        }
      }
      if (this.isTrainer) {
        // RBTs should not see prorams if all their sessions are for staff training
        // - makes the view simpler for them
        if (every(this.visibleSessions, { sessionType: 'training' })) {
          return false;
        }
      }
      return this.activePrograms.length > 0;
    }, // showPrograms()

    showRefreshAppointments() {
      return this.connectedClinic.integration
        && (
          (this.currentClient.id && this.currentClient.integrationReference)
          || ((!this.currentClient.id || this.isOwnPage) && this.userDetails.integrationReference)
        )
        && (POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration))
        && !this.isParent;
    },

    showActivities() {
      return false;
      /* For now we don't show activities on the home page @todo: Consider moving this to History instead
      return (!['parent'].includes(this.userDetails.role))
        && this.hasCurrentClient
        && (get(this.currentClient, 'type') !== 'staff');
        */
    }, // showActivities()

    activePrograms() {
      const types = [
        'simple', 'frequency', 'duration', 'interval',
        'taskAnalysis', 'instructions', 'esdm', 'asr', 'sbt',
        'set', 'ptp',
      ];

      const filteredPrograms = filter(
        this.programs,
        (program) => (
          types.includes(program.type)
          && !['waiting', 'hold', 'closed', 'discontinued'].includes(program.status)
        ),
      );

      this.naturalSort(filteredPrograms, 'programName');

      return filteredPrograms;
    }, // activePrograms()

    visibleClinicTreatmentAreas() {
      return this.clinicTreatmentAreas.filter((area) => {
        return area && (!area.isDefaultArea || (this.showDefaultTreatmentAreas && area.isVisibleArea));
      });
    }, // visibleClinicTreatmentAreas()

    treatmentAreas() {
      return chain(this.visibleClinicTreatmentAreas)
        .filter({ parent: null })
        .concat(({ id: null, label: null, order: 0 }))
        .sortBy('order')
        .map((treatmentArea) => (
          {
            ...treatmentArea,
            subTreatmentAreas: chain(this.visibleClinicTreatmentAreas)
              .filter({ parent: treatmentArea.id })
              .concat(({ id: null, label: null, order: 0 }))
              .sortBy('order')
              .reject((subTreatmentArea) => (
                isEmpty(this.getPrograms(treatmentArea.id, subTreatmentArea.id))
              ))
              .value(),
          }))
        .reject((treatmentArea) => (
          isEmpty(treatmentArea.subTreatmentAreas) && isEmpty(this.getPrograms(treatmentArea.id, null))
        ))
        .value();
    }, // treatmentAreas()

    activeScheduledSessions() {
      return reject(this.scheduledSessions, { isDeleted: true });
    }, //  activeScheduledSessions()

    visibleSessions() {
      // limitedVisibility
      return chain(this.activeScheduledSessions)
        .reject((session) => ['training', 'form'].includes(session.sessionType))
        .filter(
          (session) => (
            !session.limitedVisibility
            || [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR].includes(this.userRole)
            || (session.visibility.includes(this.userPrivilege))),
        )
        .value();
    }, // visibleSessions()

    visibleScheduledSessions() {
      return this.naturalSort(filter(this.visibleSessions, { status: 'scheduled' }), 'sessionName');
    }, // visibleScheduledSessions()

    visibleRunningSessions() {
      return this.naturalSort(chain(this.visibleSessions)
        .filter({ status: 'running' })
        .concat(this.supervisingSessions)
        .value(), 'sessionName');
    }, // visibleRunningSessions()

    visibleCreatedSessions() {
      return this.naturalSort(filter(this.visibleSessions, { status: 'created' }), 'sessionName');
    }, // visibleCreatedSessions()

    sortedActiveSessions() {
      return concat(this.visibleRunningSessions, this.visibleCreatedSessions);
    }, // sortedActiveSessions()

    hasActiveSessions() {
      return (size(this.visibleRunningSessions) + size(this.visibleCreatedSessions)) > 0;
    },

    hasScheduledSessions() {
      return size(this.visibleScheduledSessions) > 0;
    },

    formSessions() {
      return this.naturalSort(filter(this.activeScheduledSessions, { sessionType: 'form' }), 'sessionName');
    },

    trainingSessions() {
      // limitedVisibility
      return this.naturalSort(filter(this.activeScheduledSessions, { sessionType: 'training' }), 'sessionName');
    }, // trainingSessions()

    isEditingSession() {
      return (chain(this.editSession).get('sessionPrograms').size().value() > 0);
    }, // isEditingSession

    programCardSessions() {
      return this.isEditingSession ? [] : filter(this.visibleSessions, { status: 'scheduled' });
    }, // programCardSessions()

    missingTAProgramIds() {
      const treatmentAreaIds = new Set(this.visibleClinicTreatmentAreas.map(({ id }) => id));
      return this.activePrograms
        .filter(({ treatmentArea }) => treatmentArea && !treatmentAreaIds.has(treatmentArea))
        .map(({ id }) => id);
    }, // missingTAProgramIds()

  },

  watch: {
    currentClient: {
      immediate: true,
      async handler() {
        if (this.$refs.sessionBuilder) {
          this.$refs.sessionBuilder.clear();
        }
        this.loadTaskLists();
      },
    },
  },
  async created() {
    await this.handleShowProgramInfoDialog();
  },

  methods: {
    ...mapActions(auth, ['switchLoadPastUserAppointments', 'setUserAppointmentsLimit']),
    ...mapActions(clients, ['syncClients']),
    ...mapActions(units, ['loadClinicUnits']),
    ...mapActions(sessions, ['bindActiveSession', 'unbindActiveSession',
      'bindActiveSessionGroup', 'unbindActiveSessionGroup',
      'bindSessions', 'unbindSessions',
      'bindSupervisingSessions', 'unbindSupervisingSessions',
    ]),
    ...mapActions(programs, ['bindPrograms', 'unbindPrograms']),

    async loadRunningGroupSessions() {
      this.isLoadingRunningAppointments = true;
      const sessionsDb = new SessionsDB();
      const runningGroupSessions = await sessionsDb.readAll(
        [
          ['clinic', '==', this.connectedClinic.id],
          ['status', '==', 'running'],
          ['sessionGroup', '>', ''],
        ],
      );

      this.runningGroupSessionsIds = runningGroupSessions.map((session) => session.id);
      this.isLoadingRunningAppointments = false;
    },

    onChangeRole(role) {
      if (role.value === this.userDetails.role) {
        this.fakeRole = null;
        return;
      }

      this.fakeRole = role;
    }, // onChangeRole()

    onClickAppointment(appointment) {
      this.initialAppointment = get(appointment, 'id', null);
    },

    async onRefreshAppointments() {
      this.isRefreshingAppointments = true;
      try {
        const objectType = this.isOwnPage ? 'staff' : 'client';
        const objectId = this.isOwnPage
          ? this.userDetails.integrationReference
          : this.currentClient.integrationReference;

        await callFirebaseFunction('refreshAppointments', {
          clinicId: this.userDetails.clinic,
          objectType,
          objectId,
        }, {
          timeout: 540000, // timeout aligned with firebase function timeout
        });
      } catch (err) {
        const isOfflineError = ['internal', 'functions/internal'].includes(err.code)
          && err.message === 'internal'
          && !this.networkOnline;
        const errorMessage = isOfflineError ? this.$t('common.noInternetConnectionMessage') : err.message;
        notifyErrorWithDetails(errorMessage, err.details?.fullMessage);
      } finally {
        this.isRefreshingAppointments = false;
      }
    }, // onRefreshAppointments()

    /**
     * Add a program it to the session builder
     */
    async onAddProgram(session, program) {
      if (session !== null) {
        // If we are not already loading a session then
        await this.$refs.sessionBuilder.load(session);
      }

      this.$refs.sessionBuilder.add(program, true, true);
    }, // onAddProgram()

    async onAddAllProgramsToSession(session = null) {
      if (session !== null) {
        // If we are not already loading a session then
        await this.$refs.sessionBuilder.load(session);
      }
      forEach(this.activePrograms, (program) => this.$refs.sessionBuilder.add(program, true));
    }, // onAddAllProgramsToSession()

    async onProgramAction(program) {
      if (this.isInCurrentSession(program)) {
        this.$refs.sessionBuilder.remove(program);
        return;
      }
      this.onAddProgram(null, program);
    }, // onProgramAction()

    async onEditSession(session) {
      this.$refs.sessionBuilder.load(session);
    }, // onEditSession()

    async onEditSessionSchedule(session) {
      this.openComponentDialog(
        ScheduleSession,
        {
          value: session,
          tab: 'schedule',
        },
        () => { },
      );
    }, // onEditSession()

    onChangeLoadPastUserAppointments() {
      this.switchLoadPastUserAppointments();
    }, // onChangeLoadPastUserAppointments

    onLoadMoreUserAppointments() {
      this.isLoadingUserAppointments = true;
      try {
        this.setUserAppointmentsLimit(this.userAppointmentsLimit + 5);
      } finally {
        this.isLoadingUserAppointments = false;
      }
    }, // onLoadMoreUserAppointments()

    onSessionBuilderChanged(session) {
      this.editSession = session;
    }, // onSessionBuilderChanged()

    async loadTaskLists() {
      if (!this.userCanSeeTaskLists) {
        return;
      }

      const clientId = get(this.currentClient, 'id');
      if (clientId) {
        const taskListsDb = new TaskListsDB(clientId);
        await taskListsDb.bindAll(this, 'taskLists');
      } else {
        this.taskLists = [];
        const taskListsDb = new TaskListsDB(null);
        await taskListsDb.unbind(this, 'taskLists');
      }
    }, // loadTaskLists()

    programHasTargets(program) {
      return ['simple', 'frequency', 'duration', 'interval', 'esdm', 'taskAnalysis'].includes(program.type);
    }, // programHasTargets()

    isInCurrentSession(program) {
      if (isEmpty(this.editSession)) {
        return false;
      }

      return !chain(this.editSession)
        .get('sessionPrograms')
        .find((sessionProgram) => (get(sessionProgram, ['program', 'id']) === program.id))
        .isEmpty()
        .value();
    }, // isInCurrentSession()

    programActionButtonColor(program) {
      return this.isInCurrentSession(program) ? 'secondary' : 'primary';
    }, // programActionButtonColor()

    programActionButtonIcon(program) {
      return this.isInCurrentSession(program) ? 'highlight_off' : 'playlist_add';
    }, // programActionButtonIcon()

    programActionButtonLabel(program) {
      return this.isInCurrentSession(program)
        ? this.$t('programs.programCard.buttonRemoveFromSession')
        : this.$t('programs.programCard.buttonAddToSession');
    }, // programActionButtonLabel()

    programActionButtonTooltip(program) {
      return this.isInCurrentSession(program)
        ? this.$t('programs.programCard.tooltipRemoveFromSession')
        : this.$t('programs.programCard.tooltipAddToSession');
    }, // programActionButtonTooltip()

    supervisionCanBeStarted(session) {
      return this.userHasPrivilege('sessions/superviseSessions')
          && ![this.userDetails.userId, null].includes(session.runBy)
          && !session.supervisedBy;
    }, // supervisionCanBeStarted()

    getSessionUpcomingAppointments(session, onlyOwnAppointments = false) {
      return filter(this.clientAppointmentsUpcoming, (appointment) => {
        const isUpcomingAppointment = appointment.status === 'scheduled'
            && isEmpty(appointment.recurringWeekdays)
            && [null, session.id, session.originalSessionId].includes(appointment.session);

        if (!onlyOwnAppointments) {
          return isUpcomingAppointment;
        }

        return isUpcomingAppointment && appointment.user === this.userDetails.userId;
      });
    },

    getSessionClientAppointments(session) {
      if (session.status === 'running') {
        if ((session.appointment || session.supervisionAppointment) && (session.runBy !== null)) {
          const appointments = [];

          if (session.runBy === this.userDetails.userId) {
            appointments.push(...filter(this.clientAppointmentsInProgress, { session: session.id }));
          }

          if (this.supervisionCanBeStarted(session)) {
            appointments.push(...this.getSessionUpcomingAppointments(session, true));
          }

          return appointments;
        }

        // Session can be set by the current user - return appointments assigned to current user
        return this.getSessionUpcomingAppointments(session);
      }

      const startDate = new Date();
      const endDate = this.addToDate(new Date(), { days: 8 });

      const upcomingAppointments = Array.isArray(this.clientAppointmentsUpcoming)
        ? this.clientAppointmentsUpcoming.filter(
          (appointment) => appointment.status === 'scheduled'
            && appointment.type !== 'unavailability'
            && isEmpty(appointment.recurringWeekdays)
            && [null, session.id].includes(appointment.session),
        )
        : [];

      const recurringAppointments = Array.isArray(this.clientAppointmentsRecurring)
        ? this.mapRecurringAppointments(
          this.clientAppointmentsRecurring.filter((appointment) => [null, session.id].includes(appointment.session)),
          startDate,
          endDate,
        )
        : [];

      const sessionClientAppointments = [
        ...upcomingAppointments,
        ...recurringAppointments,
      ];

      if (this.currentClient.type === 'staff') {
        return sessionClientAppointments.filter((appointment) => appointment.client === this.currentClient);
      }
      return sessionClientAppointments;
    },

    getPrograms(treatmentArea, subTreatmentArea = null) {
      return this.activePrograms.filter((program) => {
        if (!treatmentArea && !subTreatmentArea && this.missingTAProgramIds.length
            && this.missingTAProgramIds.includes(program.id)) {
          return true;
        }

        const matchTreatmentArea = get(program, 'treatmentArea', null) === treatmentArea;
        const matchSubTreatmentArea = get(program, 'subTreatmentArea', null) === subTreatmentArea;

        return matchTreatmentArea && matchSubTreatmentArea;
      });
    }, // getPrograms()

    /**
     * We should remove the program info params from the URL when the dialog is closed to avoid showing it again
     */
    async handleProgramInfoDialogHide() {
      this.$router.replace({
        query: {
          ...omit(this.$route.query, ['program', 'tab']),
        },
      });
    },

    /**
     * If user navigates to the home page through a program modification notification, show the program info dialog
     */
    async handleShowProgramInfoDialog() {
      const programId = get(this.$route, ['query', 'program']);
      const defaultTab = get(this.$route, ['query', 'tab']);

      if (!this.currentClient || !programId || !defaultTab || this.offlineMode) {
        return;
      }

      try {
        this.$q.loading.show();

        const programsDb = new ProgramsDB();
        const program = await programsDb.read(programId);

        if (!program) {
          this.notifyWarning(this.$t('programs.notifyErrorLoadingProgramPreview'));

          return;
        }

        this.openComponentDialog(
          ProgramInfo,
          {
            program,
            defaultTab,
          },
          this.handleProgramInfoDialogHide,
          this.handleProgramInfoDialogHide,
          this.handleProgramInfoDialogHide,
        );
      } catch (err) {
        this.notifyWarning(this.$t('programs.notifyErrorLoadingProgramPreview'));
      } finally {
        this.$q.loading.hide();
      }
    }, // handleShowProgramInfoDialog()
  },

};
</script>
